import React, { useEffect, useState } from "react";
import { TextInput, Select, Text, Skeleton } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getValueForInput,
  loadingStates,
  existsAndLength,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { addTemplate, templatesListApi } from "../../utilities/apis/templates";

function TemplateForm() {
  const arr = useLocation().pathname.split("/");
  let type = arr[arr.length - 2];
  type = type.replace(
    /\w+/g,
    (w) => w[0].toUpperCase() + w.slice(1).toLowerCase(),
  );

  const [input, setInput] = useState({
    templateName: "",
    templateCategory: "",
  });

  const changeHandler = (name) => (inputValue) => {
    const value = getValueForInput(inputValue);
    setInput((data) => ({
      ...data,
      [name]: value,
    }));
  };

  const [uiConfigs, setUiConfigs] = useState({
    templateCategories: [],
    loading: loadingStates.NO_ACTIVE_REQUEST,
    loadingCategories: loadingStates.NO_ACTIVE_REQUEST,
    errors: {},
  });

  const validate = () => {
    const keys = {};
    if (!existsAndLength(input.templateName)) {
      keys.templateName = "Please enter a template name.";
    }
    if (!existsAndLength(input.templateCategory)) {
      keys.templateCategory = "Please select or create a category.";
    }

    if (Object.keys(keys).length) {
      showNotification({
        color: "red",
        title: "Template Form",
        message: "Make sure all fields are filled properly.",
      });
      setUiConfigs((data) => ({
        ...data,
        errors: keys,
      }));
      return false;
    }
    return true;
  };

  const getTemplatesList = async () => {
    setUiConfigs((prevState) => ({
      ...prevState,
      loadingCategories: loadingStates.LOADING,
    }));
    const response = await apiWrapWithErrorWithData(templatesListApi({}));
    if (response?.success && response?.templateCategories) {
      setUiConfigs((prev) => ({
        ...prev,
        loadingCategories: loadingStates.NO_ACTIVE_REQUEST,
        templateCategories: response.templateCategories.map(
          (template) => template.category,
        ),
      }));
    } else {
      setUiConfigs((prev) => ({
        ...prev,
        loadingCategories: loadingStates.NO_ACTIVE_REQUEST,
      }));
    }
  };

  const navigate = useNavigate();
  const listPath =
    type === "Agreements"
      ? "/app/agreements_manager/templates/agreement"
      : "/app/agreements_manager/notice";

  const addTemplateHandler = async () => {
    if (!validate()) return;

    setUiConfigs((data) => ({
      ...data,
      errors: {},
      loading: loadingStates.LOADING,
    }));
    const response = await apiWrapWithErrorWithData(
      addTemplate({
        type,
        name: input.templateName,
        category: input.templateCategory,
        html: "",
      }),
    );
    if (response?.success && response?.template) {
      setUiConfigs((data) => ({
        ...data,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      }));
      showNotification({
        color: "green",
        title: "Template Created.",
        message: "Template has been created.",
      });
      if (type === "Agreements") {
        navigate(`/app/templates/edit/template/${response.template.id}`, {
          state: { isNew: true },
        });
      } else {
        navigate("/app/templates/notice");
      }
    } else {
      showNotification({
        color: "red",
        title: "Template Record",
        message: "Template could not be created.",
      });
      setUiConfigs((data) => ({
        ...data,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      }));
    }
  };

  useEffect(() => {
    getTemplatesList();
  }, []);

  const isSaving = uiConfigs.loading === loadingStates.LOADING;

  return (
    <div className=" ">
      <button
        type="button"
        onClick={() => navigate(listPath)}
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
      >
        <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4">
          <path
            d="M12.5 4.5L7 10l5.5 5.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back to templates
      </button>

      <div className="mb-6">
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">
          Create template
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Add a name and category, then continue to the editor to curate the
          document.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex flex-col gap-6">
          <TextInput
            label="Template name"
            placeholder="Enter the template name"
            value={input.templateName}
            onChange={changeHandler("templateName")}
            error={uiConfigs.errors.templateName}
            classNames={{
              label: "mb-1.5 text-sm font-medium text-slate-700",
              input:
                "h-11 rounded-lg border-slate-200 text-slate-900 placeholder:text-slate-400",
            }}
          />

          {uiConfigs.loadingCategories === loadingStates.LOADING && (
            <Skeleton height={72} radius="md" />
          )}

          {uiConfigs.loadingCategories === loadingStates.NO_ACTIVE_REQUEST && (
            <Select
              creatable
              searchable
              getCreateLabel={(query) => (
                <Text className="text-[#1a8fb3]">+ Create “{query}”</Text>
              )}
              data={uiConfigs.templateCategories}
              placeholder="Select or type a category"
              label="Category"
              value={input.templateCategory}
              onCreate={(query) => {
                setUiConfigs((prevState) => ({
                  ...prevState,
                  templateCategories: [...prevState.templateCategories, query],
                }));
                return query;
              }}
              onChange={changeHandler("templateCategory")}
              error={uiConfigs.errors.templateCategory}
              classNames={{
                label: "mb-1.5 text-sm font-medium text-slate-700",
                input:
                  "h-11 rounded-lg border-slate-200 text-slate-900 placeholder:text-slate-400",
              }}
            />
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-6">
          <button
            type="button"
            onClick={() => navigate(listPath)}
            disabled={isSaving}
            className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={addTemplateHandler}
            disabled={isSaving}
            className="inline-flex h-10 min-w-[140px] items-center justify-center rounded-lg bg-[#46BDE1] px-4 text-sm font-medium text-white hover:bg-[#3aa8ca] disabled:opacity-60"
          >
            {isSaving ? (
              <BeatLoader color="#fff" size={8} />
            ) : (
              "Create template"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TemplateForm;
